import type { ThemeShape } from "./theme/theme";
import { drawerWidths } from "../../../../../shared/frontend/components/drawerWidths";
import { roundedControlThemeTokens } from "../../../../../shared/frontend/theme/appearance";
/**
 * m1member Design System
 * 
 * Diese Datei enthält die zentralen UI-Tokens, Layout-Defaults und Komponenten-Standards der m1member-App.
 * Theme-Domäne und Benutzer-Theme liegen in `app/theme/theme.ts`.
 */

// ============================================================================
// TOKENS
// ============================================================================

// ============================================================================
// SPACING & LAYOUT
// ============================================================================

/**
 * Basis-Abstands-Skala (in px) — die EINZIGE Quelle für Standardabstände.
 *
 * Alle semantischen Abstands-Tokens unten werden aus dieser Skala abgeleitet,
 * nie als eigene Literale dupliziert. Ändert sich hier ein Wert, ziehen alle
 * abgeleiteten Tokens automatisch nach.
 */
const scale = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32
} as const;

export const spacing = {
  // Standard Spacing-Scale (in px)
  ...scale,

  // Werttreue, fachlich benannte Zwischenwerte für bestehende kompakte UI-Verträge.
  hairline: 1,
  micro: 2,
  compact: 3,
  minor: 5,
  controlCompact: 6,
  controlInset: 7,
  controlGap: 10,
  relaxed: 14,
  navigationGap: 18,
  contentGap: 20,
  previewPadding: 40,
  mobileClearance: 56,
  /** ProLayout-Kopfzeile (`heightLayoutHeader`) und Höhe der Desktop-Titelleiste. Spiegel: `--m1-header-bar-height`. */
  headerBarHeight: 56,

  // Komponenten-spezifische Abstände (aus `scale` abgeleitet — keine eigenen Literale)
  /** Nur für Layouts ohne AppLayout-Shell (z. B. Login). Haupt-App: Außenabstand kommt aus dem Shared-`AppShell`. */
  pagePadding: scale.xl,
  /** Unterer AppLayout-Content-Abstand für lange Arbeitsseiten (Sonderwert, nicht auf der Skala). */
  appContentPaddingBottom: 48,
  /** Standardabstand unter Seitentiteln. */
  pageHeaderMarginBottom: scale.lg,
  /** Standardabstand zwischen vertikal gestapelten Sections. */
  sectionMarginTop: scale.lg,
  cardPadding: scale.lg,           // Padding in Cards (allgemein)
  /** Großzügiges Padding für Inhaltskarten, nicht für normale SettingsListPage-Listen. */
  listCardPadding: scale.xl,
  /** Kompaktes Padding für normale SettingsListPage-Listen. */
  listCardPaddingCompact: scale.md,
  formItemGap: scale.md,           // Gap zwischen Form-Items (Grid)
  drawerWidth: drawerWidths.standard,
  drawerContentPadding: scale.lg,  // Padding im Drawer-Content
  loginCardWidth: 440,       // Breite des Login-Cards (Sonderwert)
  loginCardPadding: scale.xl,      // Padding um Login-Card

  // Layout-Gaps
  gridGap: scale.lg,               // Gap in Grid-Layouts (Dashboard, etc.)
  sectionGap: scale.lg,            // Abstand zwischen Sections
  elementGap: scale.md,            // Abstand zwischen Elementen (Space-Component)

  // Divider Spacing (aus der Skala zusammengesetzt)
  // Oben xl: FormSectionHeading steht oft direkt hinter einem ResponsiveFormGrid,
  // dessen Form.Items marginBottom 0 haben — der Divider trägt dann denselben
  // Abstand allein, den ein Form.Item sonst nach unten lassen würde.
  dividerMargin: `${scale.xl}px 0 ${scale.md}px`,
  dividerMarginCompact: `${scale.sm}px 0`          // Kompakter Divider-Margin
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  // Font Sizes (Ant Design Standard)
  fontSize: {
    micro: 8,
    tiny: 9,
    xxs: 10,
    xs: 11,
    sm: 12,
    compact: 13,
    base: 14,
    emphasized: 15,
    md: 16,
    subtitle: 18,
    lg: 20,
    title: 22,
    xl: 24,
    display: 28,
    xxl: 32,
    hero: 64
  },
  
  // Title Levels (Ant Design)
  title: {
    level1: { fontSize: 38, lineHeight: 1.23, fontWeight: 600 },
    level2: { fontSize: 30, lineHeight: 1.35, fontWeight: 600 },
    level3: { fontSize: 24, lineHeight: 1.35, fontWeight: 600 },
    level4: { fontSize: 20, lineHeight: 1.4, fontWeight: 600 },
    level5: { fontSize: 16, lineHeight: 1.5, fontWeight: 600 }
  },
  
  // Text Variants
  text: {
    primary: "rgba(0, 0, 0, 0.88)",      // Light mode
    secondary: "rgba(0, 0, 0, 0.65)",    // Light mode
    disabled: "rgba(0, 0, 0, 0.25)",     // Light mode
    // Dark mode wird über CSS-Variablen gehandhabt
  },
  
  // Font Weights
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  
  // Line Heights
  lineHeight: {
    icon: 1,
    compact: 1.15,
    tight: 1.2,
    medium: 1.3,
    dense: 1.35,
    normal: 1.4,
    relaxed: 1.5,
    loose: 1.6
  },
  
  // Font Variant Numeric (für Zahlen/Tabellen)
  fontVariantNumeric: "tabular-nums" as const
} as const;

// ============================================================================
// COMPONENT PATTERNS
// ============================================================================

/**
 * ProTable m1member-Standards
 * 
 * Nur Werte, die vom Ant Design Standard abweichen oder explizit gesetzt werden müssen.
 * 
 * @example
 * ```tsx
 * <ProTable<RowType>
 *   actionRef={actionRef}
 *   rowKey="id"
 *   options={false}  // ProTable hat standardmäßig Optionen aktiviert
 *   search={false}   // ProTable hat standardmäßig Suche aktiviert
 *   pagination={{ pageSize: 20 }}
 *   toolBarRender={() => [
 *     <Button key="new" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
 *       Neu anlegen
 *     </Button>
 *   ]}
 *   request={async () => {
 *     const list = (await auth.api.get<RowType[]>("/api/v1/...")) || [];
 *     return { data: list, success: true, total: list.length };
 *   }}
 *   columns={columns}
 * />
 * ```
 * 
 * @example Option-Spalte
 * ```tsx
 * const columns: ProColumns<RowType>[] = [
 *   { title: "Name", dataIndex: "name" },
 *   {
 *     title: "",
 *     valueType: "option",
 *     width: proTableDefaults.optionColumnWidth,  // 220px
 *     render: (_, r) => [
 *       <Button key="edit" type="link" onClick={() => openEdit(r)}>Bearbeiten</Button>,
 *       <Button key="del" type="link" danger onClick={() => del(r)}>Löschen</Button>
 *     ]
 *   }
 * ];
 * ```
 */
export const proTableDefaults = {
  // Abweichende Standard-Werte (ProTable hat diese standardmäßig aktiviert)
  options: false,              // ProTable Standard: true
  search: false,               // ProTable Standard: true
  
  // Spezifische Werte
  optionColumnWidth: 220,      // Standard-Breite für Option-Spalte
  paginationPageSize: 20       // Standard Page Size
} as const;

/**
 * Drawer m1member-Standards
 * 
 * WICHTIG: Header-Aktionen über `extra` (nicht Footer-Buttons).
 * 
 * @example
 * ```tsx
 * <Drawer
 *   width={drawerDefaults.width}  // "min(720px, 96vw)"
 *   destroyOnClose  // Form-Reset beim Schließen
 *   title={edit ? "Bearbeiten" : "Anlegen"}
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   extra={
 *     <Space gap={spacing.elementGap}>
 *       {edit ? (
 *         <Button danger onClick={onDelete}>Löschen</Button>
 *       ) : null}
 *       <Button onClick={() => setOpen(false)}>Abbrechen</Button>
 *       <Button type="primary" loading={saving} onClick={onSave}>
 *         {edit ? "Speichern" : "Anlegen"}
 *       </Button>
 *     </Space>
 *   }
 * >
 *   {/* Drawer Content *\/}
 * </Drawer>
 * ```
 */
export const drawerDefaults = {
  narrowWidth: drawerWidths.standard,
  width: spacing.drawerWidth,  // "min(720px, 96vw)" - spezifische Breite
  wideWidth: drawerWidths.wide,
  destroyOnClose: true          // Wichtig für Form-Reset
} as const;

/**
 * Form m1member-Standards
 * 
 * @example Basis-Form
 * ```tsx
 * <Form layout="vertical" requiredMark={false}>  // requiredMark: false ist NICHT Standard
 *   <Form.Item name="name" label="Name" rules={[{ required: true, message: "Erforderlich" }]}>
 *     <Input />
 *   </Form.Item>
 * </Form>
 * ```
 * 
 * @example 2-Spalten-Grid (im Drawer)
 * ```tsx
 * <Form layout="vertical" requiredMark={false}>
 *   <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.formItemGap }}>
 *     <Form.Item name="name" label="Name">
 *       <Input />
 *     </Form.Item>
 *     <Form.Item name="code" label="Code">
 *       <Input />
 *     </Form.Item>
 *   </div>
 * </Form>
 * ```
 * 
 * @example Section-Trennung
 * ```tsx
 * <Form layout="vertical" requiredMark={false}>
 *   <Form.Item name="name" label="Name">
 *     <Input />
 *   </Form.Item>
 *   <FormSectionHeading title={text("Section")} />
 *   <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.formItemGap }}>
 *     {/* Weitere Felder *\/}
 *   </div>
 * </Form>
 * ```
 */
export const formDefaults = {
  requiredMark: false,  // Ant Design Standard: true (zeigt rote Sternchen)
  itemGap: spacing.formItemGap,
  sectionGap: spacing.sectionGap,
  twoColumnGridGap: spacing.formItemGap,  // Gap für 2-Spalten-Grid: 12px
  twoColumnMinWidth: 260
} as const;

/**
 * Button Patterns (m1member-Konventionen)
 * 
 * Konventionen für Button-Verwendung (Werte sind Ant Design Standard):
 * - **Primary CTA**: `type="primary"` - Hauptaktionen (Toolbar)
 * - **Links in Tabellen/Optionen**: `type="link"` - Sekundäre Aktionen
 * - **Destruktiv**: `type="link" danger` oder `danger` - Löschen/Destruktive Aktionen
 * 
 * @example
 * ```tsx
 * // Primary Button (Toolbar)
 * <Button type="primary" icon={<PlusOutlined />}>
 *   Neu anlegen
 * </Button>
 * 
 * // Link Buttons (Option-Spalte)
 * <Button type="link" onClick={handleEdit}>Bearbeiten</Button>
 * <Button type="link" danger onClick={handleDelete}>Löschen</Button>
 * 
 * // Danger Button (Drawer Header)
 * <Button danger onClick={handleDelete}>Löschen</Button>
 * ```
 */
// Keine Export-Werte nötig - alle Werte sind Ant Design Standard

/**
 * Card m1member-Standards
 *
 * Listen-/Arbeitsflächen-Karten:
 * - Normale Arbeitslisten: listCardBodyStyleCompact (12px).
 * - Großzügige Inhaltskarten: listCardBodyStyle (24px), nicht für normale SettingsListPage-Listen.
 * - Abstand unter Kopfzeile (Titel + Button): listCardHeaderMarginBottom.
 *
 * @example Login Card
 * ```tsx
 * <Card style={{ width: cardDefaults.loginWidth, maxWidth: "100%" }} bordered>
 *   {/* Login Content *\/}
 * </Card>
 * ```
 *
 * @example Listen-Karte (Personen/Detail, Einstellungen)
 * ui-standard-exception: section-heading — Kartentitel im Doku-Beispiel, kein Formularabschnitt.
 * ```tsx
 * <Card bodyStyle={cardDefaults.listCardBodyStyleCompact}>
 *   <Flex justify="space-between" align="center" style={{ marginBottom: cardDefaults.listCardHeaderMarginBottom }}>
 *     <Typography.Title level={5} style={{ margin: 0 }}>…</Typography.Title>
 *     <Button>…</Button>
 *   </Flex>
 *   <SettingsListPage headerTitle={…} headerExtra={…} … />
 * </Card>
 * ```
 *
 * @example Dashboard Statistic Cards
 * ```tsx
 * <Card bordered style={{ marginBottom: 0, height: "100%" }}>
 *   <Statistic title="..." value={...} />
 * </Card>
 * ```
 */
export const cardDefaults = {
  loginWidth: spacing.loginCardWidth,  // 440px - spezifische Breite für Login
  sectionBodyStyle: { padding: spacing.cardPadding } as const,
  sectionBodyStyleCompact: { padding: spacing.listCardPaddingCompact } as const,
  dashboardBodyStyle: { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 } as const,
  /** Großzügiger bodyStyle nur für Inhaltskarten, nicht für normale Listen (24px). */
  listCardBodyStyle: { padding: spacing.listCardPadding } as const,
  /** Standard-bodyStyle für normale Arbeitslisten (12px). */
  listCardBodyStyleCompact: { padding: spacing.listCardPaddingCompact } as const,
  /** Abstand zwischen Karten-Überschrift (Titel + Button) und Tabelleninhalt. */
  listCardHeaderMarginBottom: 12
} as const;

// ============================================================================
// LAYOUT PATTERNS (nur spezifische Werte)
// ============================================================================

/**
 * Login Page Layout (spezifische Werte)
 */
export const loginLayout = {
  cardWidth: spacing.loginCardWidth,  // 440px
  containerPadding: spacing.pagePadding  // 24px
} as const;

export const layoutDefaults = {
  /** Innenabstand des Inhalts kommt aus dem Shared-`AppShell`, nicht von hier. */
  appContentStyle: {
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0
  } as const,
  pageHeaderMarginBottom: spacing.pageHeaderMarginBottom,
  sectionMarginTop: spacing.sectionMarginTop,
  mobileNavigationDrawerWidth: "min(88vw, 360px)"
} as const;

// ============================================================================
// CSS CLASSES & GLOBAL STYLES
// ============================================================================

/**
 * CSS-Klassen-Namen (wie in global.css definiert)
 */
export const cssClasses = {
  personPhotoIcons: "person-photo-icons",
  personPhotoContainer: "person-photo-container"
} as const;

/**
 * CSS Custom Properties / CSS Variables
 * (werden von Ant Design gesetzt, hier als Referenz)
 */
export const cssVariables = {
  // Ant Design CSS Variables werden automatisch generiert
  // Wichtig: data-theme Attribut auf <html> setzen für Dark Mode
  themeAttribute: "data-theme",
  colorScheme: "color-scheme"
} as const;

// ============================================================================
// GLOBAL CSS REGELS (als Referenz)
// ============================================================================

/**
 * Globale CSS-Regeln (aus global.css)
 * Diese sollten in das neue Projekt kopiert werden
 */
export const globalCssRules = {
  root: {
    colorScheme: "light",
    darkMode: {
      colorScheme: "dark",
      selector: ':root[data-theme="dark"]'
    }
  },
  
  htmlBodyRoot: {
    height: "100%"
  },
  
  body: {
    margin: 0,
    background: "transparent"
  }
} as const;

// ============================================================================
// ANIMATIONS & TRANSITIONS
// ============================================================================

export const animations = {
  transition: {
    fast: "120ms ease-in-out",
    normal: "200ms ease-in-out",
    slow: "300ms ease-in-out"
  },
  
  hover: {
    opacity: {
      from: 0,
      to: 1,
      duration: "120ms ease-in-out"
    }
  }
} as const;

// ============================================================================
// BORDER RADIUS
// ============================================================================

export const borderRadius = {
  none: 0,
  hairline: 1,
  xs: 2,
  sm: 3,
  control: 4,
  compact: 5,
  md: 6,
  lg: 8,
  xl: 10,
  xxl: 12,
  pill: 24,
  legacyPill: 999,
  full: 9999,
  
  // Theme-abhängig
  fromTheme: (shape: ThemeShape) => shape === "square" ? 0 : roundedControlThemeTokens.borderRadius
} as const;

// ============================================================================
// Z-INDEX SCALE
// ============================================================================

export const zIndex = {
  dropdown: 1050,
  sticky: 1020,
  fixed: 1030,
  modal: 1000,
  popover: 1030,
  tooltip: 1070,
  desktopHeader: 11000,
  desktopPopup: 12000,
  
  // Calendar spezifisch
  calTime: 5,
  calHeaderCell: 4,
  calApptSpan: 3
} as const;

// ============================================================================
// EXPORT: Komplette Design-System-Konfiguration
// ============================================================================

export const designSystem = {
  spacing,
  typography,
  components: {
    proTable: proTableDefaults,
    drawer: drawerDefaults,
    form: formDefaults,
    card: cardDefaults
  },
  layouts: {
    app: layoutDefaults,
    login: loginLayout
  },
  css: {
    classes: cssClasses,
    variables: cssVariables,
    global: globalCssRules
  },
  animations,
  borderRadius,
  zIndex
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gibt Spacing-Wert zurück
 */
export function getSpacing(key: keyof typeof spacing): number | string {
  return spacing[key];
}

/** Erzeugt einen einzelnen CSS-Pixelwert aus einem benannten Design-Token. */
export function cssPixels(value: number): string {
  return `${value}px`;
}

/** Erzeugt eine werttreue CSS-Spacing-Kurzschreibweise aus benannten Design-Tokens. */
export function cssSpacing(...values: Array<number | "auto">): string {
  return values.map((value) => typeof value === "number" ? `${value}px` : value).join(" ");
}

/**
 * Gibt Border Radius basierend auf Theme Shape zurück
 */
export function getBorderRadius(shape: ThemeShape): number {
  return borderRadius.fromTheme(shape);
}
